import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { buildingsSample, cognitoRole } from "data/queries-key";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";

import { industries, workDescription } from "components/user/sign-in/states";
import { useIdentity } from "contexts/identity-context";
import type {
  BuildingsSampleRequest,
  Industry,
  SampleDatum,
  SampleFormat,
  WorkDescription,
} from "data/models";
import { submitBuildingsSample, updateCognitoRole } from "data/mutations";
import { ArrowDownToLine } from "lucide-react";
import { Link } from "react-router-dom";

interface DownloadModalProps {
  open: boolean;
  onClose: () => void;
  onDownload: (presignedUrl: string) => void;
}

export const DownloadModal = ({
  open,
  onClose,
  onDownload,
}: DownloadModalProps) => {
  const theme = useTheme();
  const [identityState, setIdentity] = useIdentity();
  const queryClient = useQueryClient();

  const [requestReason, setRequestReason] = useState("");
  const [workDescriptionValue, setWorkDescriptionValue] = useState<
    WorkDescription | ""
  >("");
  const [industry, setIndustry] = useState<Industry>("Property & Real Estate");
  const [canContact, setCanContact] = useState(false);
  const [datum, setDatum] = useState<SampleDatum>("GDA2020");
  const [format, setFormat] = useState<SampleFormat>("GPKG");
  const [hasInitialized, setHasInitialized] = useState(false);

  // Fetch initial work description from Cognito attributes only once
  useEffect(() => {
    if (identityState && identityState !== "loading" && !hasInitialized) {
      const cognitoRole = identityState.role;
      if (
        cognitoRole &&
        workDescription.includes(cognitoRole as WorkDescription)
      ) {
        setWorkDescriptionValue(cognitoRole as WorkDescription);
      } else {
        setWorkDescriptionValue("");
      }
      setHasInitialized(true);
    }
  }, [identityState, hasInitialized]);

  const updateRoleMutation = useMutation({
    mutationKey: [cognitoRole],
    mutationFn: ({ cognitoUser, role }: { cognitoUser: any; role: string }) =>
      updateCognitoRole(cognitoUser, role),
    onSuccess: (updatedRole: string) => {
      if (identityState && identityState !== "loading") {
        const updatedIdentityState = {
          ...identityState,
          role: updatedRole,
        };
        setIdentity(updatedIdentityState);
        setWorkDescriptionValue(updatedRole as WorkDescription);
      }
    },
    onError: (error: Error) => {
      console.error("Failed to update custom:role:", error);
    },
  });

  const buildingsSampleMutation = useMutation({
    mutationKey: [buildingsSample],
    mutationFn: submitBuildingsSample,
    onSuccess: (presignedUrl: string) => {
      onDownload(presignedUrl);
      handleModalClose();
    },
    onError: (error: Error) => {
      console.error("Buildings sample request failed:", error);
    },
  });

  const handleModalClose = () => {
    setRequestReason("");
    setWorkDescriptionValue("");
    setIndustry("Property & Real Estate");
    setCanContact(false);
    setDatum("GDA2020");
    setFormat("GPKG");
    setHasInitialized(false); // Reset initialization flag
    onClose();
  };

  const handleDownload = () => {
    // First update Cognito role if needed, then submit buildings sample
    const requestData: BuildingsSampleRequest = {
      requestReason: requestReason.trim(),
      industry,
      canContact,
      datum,
      format,
    };
    if (
      workDescriptionValue &&
      workDescriptionValue !== "" &&
      identityState &&
      identityState !== "loading"
    ) {
      updateRoleMutation.mutate(
        {
          cognitoUser: identityState.cognitoUser,
          role: workDescriptionValue,
        },
        {
          onSuccess: () => {
            buildingsSampleMutation.mutate(requestData);
          },
        }
      );
    } else {
      // If no role update needed, just submit the buildings sample
      buildingsSampleMutation.mutate(requestData);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      aria-labelledby="download-modal-title"
      aria-describedby="download-modal-description"
    >
      <Card
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          // width: 600,
          maxHeight: "90vh",
          overflowY: "auto",
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(33, 33, 33, 0.95)"
              : "rgba(255, 255, 255, 0.95)",
        }}
      >
        {/* Header with download icon and title */}

        <CardHeader
          title={
            <Box display="flex" alignItems="center">
              <ArrowDownToLine size={"32px"} style={{ marginRight: 8 }} />
              Unlock Your National Buildings Sample
            </Box>
          }
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        />

        {/* Explanation text */}
        <CardContent
          sx={{ display: "flex", flexDirection: "column", gap: "16px" }}
        >
          <Typography
            sx={{
              color: theme.palette.text.primary,
              fontSize: "16px",
              lineHeight: 1.5,
            }}
          >
            To help us provide the most relevant resources and support, please
            share a little more about how you plan to use this data. Your sample
            download will be enabled once you complete these fields.
          </Typography>

          {/* Use case text field */}
          <Box>
            <TextField
              fullWidth
              multiline
              label="What problem are you hoping to solve or insight are you looking to gain with this data?"
              rows={3}
              placeholder="e.g., Market research, trend analysis, predictive modeling, internal reporting..."
              value={requestReason}
              onChange={(e) => setRequestReason(e.target.value)}
              variant="outlined"
              helperText="Understanding your use case helps us tailor future resources and product improvements."
              error={buildingsSampleMutation.isError}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: theme.palette.background.paper,
                },
              }}
            />
          </Box>

          {/* Work description dropdown */}
          <Box>
            <FormControl fullWidth size="small">
              <InputLabel id="work-description-label">
                What best describes your work?
              </InputLabel>
              <Select
                labelId="work-description-label"
                id="work-description-multi-select"
                value={workDescriptionValue}
                onChange={(e) =>
                  setWorkDescriptionValue(e.target.value as WorkDescription)
                }
                label="What best describes your work?"
                variant="outlined"
                displayEmpty
                sx={{
                  backgroundColor: theme.palette.background.paper,
                }}
              >
                {workDescription
                  .sort((a, b) => a.localeCompare(b))
                  .map((service, index) => (
                    <MenuItem key={index} value={service}>
                      {service}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>

          {/* Industry dropdown */}
          <Box>
            <FormControl fullWidth size="small">
              <InputLabel id="industry-label">
                What industry best describes your organization?
              </InputLabel>
              <Select
                labelId="industry-label"
                value={industry}
                onChange={(e) => setIndustry(e.target.value as Industry)}
                variant="outlined"
                label="What industry best describes your organization?"
                displayEmpty
                sx={{
                  backgroundColor: theme.palette.background.paper,
                }}
              >
                {industries.map((industryOption) => (
                  <MenuItem key={industryOption} value={industryOption}>
                    {industryOption}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Contact checkbox */}
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  sx={{ marginLeft: "9px" }}
                  checked={canContact}
                  onChange={(e) => setCanContact(e.target.checked)}
                />
              }
              label={
                <Typography sx={{ color: theme.palette.text.primary }}>
                  I'm interested in learning more. Please contact me about this
                  data.
                </Typography>
              }
            />
            <Typography
              sx={{
                marginLeft: "8px",
                color: theme.palette.text.secondary,
                fontSize: "12px",
              }}
            >
              Optional
            </Typography>
          </Box>

          {/* Sample Datum dropdown */}
          <Box>
            <FormControl fullWidth size="small">
              <InputLabel id="datum-label">Sample Datum</InputLabel>
              <Select
                labelId="datum-label"
                value={datum}
                onChange={(e) => setDatum(e.target.value as SampleDatum)}
                variant="outlined"
                label="Sample Datum"
                sx={{
                  backgroundColor: theme.palette.background.paper,
                }}
              >
                <MenuItem value="GDA2020">GDA 2020</MenuItem>
                <MenuItem value="GDA94">GDA 94</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Sample Format dropdown */}
          <Box>
            <FormControl fullWidth size="small">
              <InputLabel id="format-label">Sample Format</InputLabel>
              <Select
                labelId="format-label"
                value={format}
                onChange={(e) => setFormat(e.target.value as SampleFormat)}
                variant="outlined"
                label="Sample Format"
                sx={{
                  backgroundColor: theme.palette.background.paper,
                }}
              >
                <MenuItem value="GPKG">GeoPackage</MenuItem>
                <MenuItem value="SHP">Shapefile</MenuItem>
                <MenuItem value="GEOJSON">GeoJSON</MenuItem>
                <MenuItem value="TAB">MapInfo TAB</MenuItem>
                <MenuItem value="GDB">File Geodatabase</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Note */}
          <Typography
            sx={{
              marginLeft: "12px",
              color: theme.palette.text.secondary,
              fontSize: "12px",
            }}
          >
            <strong>Note:</strong> Insight packs are aspatial PSV (Pipe
            Separated Values)
          </Typography>

          <Typography variant="caption">
            This sample is provided so you can explore our data. It is 'Sample
            Data' governed by our{" "}
            <Link
              to={{
                pathname: "https://geoscape.com.au/terms-of-use/",
              }}
              target="_blank"
              rel="noopener"
              style={{ color: theme.palette.primary.main }}
            >
              Geoscape Terms of Use
            </Link>{" "}
            (see clause 29 'Sample Data Licence'), which means you may only use
            it for one month for evaluation, not for business or commercial
            purposes. By downloading, you agree to use the data as Sample Data
            in accordance with the{" "}
            <Link
              to={{
                pathname: "https://geoscape.com.au/terms-of-use/",
              }}
              target="_blank"
              rel="noopener"
              style={{ color: theme.palette.primary.main }}
            >
              Geoscape Terms of Use
            </Link>
            .
          </Typography>

          {/* Error Alert */}
          {(buildingsSampleMutation.isError || updateRoleMutation.isError) && (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
              {buildingsSampleMutation.isError &&
              buildingsSampleMutation.error instanceof Error
                ? buildingsSampleMutation.error.message
                : updateRoleMutation.isError &&
                  updateRoleMutation.error instanceof Error
                ? updateRoleMutation.error.message
                : "An error occurred while processing your request. Please try again."}
            </Alert>
          )}

          {/* Action buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Button
              onClick={handleModalClose}
              variant="text"
              size="small"
              disabled={
                buildingsSampleMutation.isLoading ||
                updateRoleMutation.isLoading
              }
            >
              Cancel
            </Button>
            <Button
              onClick={handleDownload}
              variant="contained"
              size="small"
              disabled={
                !requestReason.trim() ||
                !workDescriptionValue ||
                workDescriptionValue === "" ||
                buildingsSampleMutation.isLoading ||
                updateRoleMutation.isLoading
              }
              startIcon={
                buildingsSampleMutation.isLoading ||
                updateRoleMutation.isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
            >
              {buildingsSampleMutation.isLoading || updateRoleMutation.isLoading
                ? "Preparing..."
                : "Download"}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Modal>
  );
};
